import type { AfterViewInit, OnDestroy, TemplateRef } from '@angular/core';
import {
    ChangeDetectionStrategy,
    Component,
    ViewChild,
    ViewContainerRef,
    computed,
    inject,
    input,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { Store } from '@ngrx/store';

import type { FormInputSettingsData } from '@sites/entity-core';
import type { FormWidgetForm } from '@sites/entity-signal-form';

import type { WebLegacyWidgetEntity } from '@web/types';
import type { FormWidgetData } from '@web/widgets/form/type';

import { previewPortalAttachAction } from '@web/store/page-structure/widgets/widgets.actions';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { SpFormInputSettingsSignalComponent } from '@components/sp-form-input-settings-signal';
import { FormModalPreviewComponent } from './form-modal/form-modal-preview.component';

import { FormSettingsPanel } from '@web/widgets/common/abstract/form-settings-panel';

/**
 * Breadcrumbs/footer chrome around `sp-form-input-settings-signal`, writing
 * straight into the widget signal form. Save/cancel and the transaction frame
 * come from `FormSettingsPanel`. The modal preview is rendered through a portal
 * and reads a live widget snapshot from the form so it tracks edits.
 */
@Component({
    selector: 'web-form-input-settings',
    templateUrl: './form-input-settings.component.html',
    styleUrls: ['./form-input-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, SpFormInputSettingsSignalComponent, FormModalPreviewComponent],
})
export class FormInputSettingsComponent extends FormSettingsPanel<FormWidgetForm> implements AfterViewInit, OnDestroy {
    public readonly isModalForm = input<boolean>(false);
    public readonly isTwoStepForm = input<boolean>(false);

    @ViewChild('modalPreviewPortal') public modalPreviewPortal: TemplateRef<unknown>;

    private readonly store$: Store = inject(Store);
    private readonly document: Document = inject(DOCUMENT);
    private readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
    private portalHost: DomPortalOutlet;

    public readonly settings = computed<FormInputSettingsData>(() => this.form().inputSettings().value());

    /** Live widget snapshot for the display-only modal preview. */
    public readonly widgetSnapshot = computed(
        () => this.form()._raw().value() as unknown as WebLegacyWidgetEntity<FormWidgetData>
    );

    public onSettingsChange(value: FormInputSettingsData): void {
        this.form().setInputSettings(value);
    }

    public ngAfterViewInit(): void {
        if (this.isModalForm()) {
            const portalHTMLElement = this.document.querySelector('#modalPreviewContent');
            this.portalHost = new DomPortalOutlet(portalHTMLElement);

            this.portalHost.attach(new TemplatePortal(this.modalPreviewPortal, this.viewContainerRef));
            this.store$.dispatch(previewPortalAttachAction.modalPreviewToggle({ attached: true }));
        }
    }

    public ngOnDestroy(): void {
        if (this.isModalForm()) {
            this.portalHost.detach();
            this.store$.dispatch(previewPortalAttachAction.modalPreviewToggle({ attached: false }));
        }
    }
}
